import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Tooltip,
  Chip,
  Avatar,
  Stack,
  alpha,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import FeatherIcon from 'feather-icons-react';

const columnStyles = {
  company_name: { width: '220px !important' },
  category: { width: '160px !important' },
  description: { width: '260px !important' },
  representatives: { width: '220px !important' },
  properties: { width: '260px !important' },
  actions: { width: '140px !important', textAlign: 'center' },
};

const tableTheme = createTheme({
  typography: {
    fontFamily:
      "'Inter var', 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    body2: {
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    caption: {
      fontWeight: 500,
      letterSpacing: '0.02em',
    },
  },
});


const ContractorsListTable = ({ items = [], onEdit, onDelete, userRole }) => {
  const isCompanyUser = userRole === 'company';

  const columns = [
    {
      dataField: 'company_name',
      text: 'Company Name',
      sort: true,
      headerStyle: () => ({ ...columnStyles.company_name, fontWeight: 600 }),
      style: columnStyles.company_name,
      formatter: (cell) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: 'primary.dark',
              fontSize: '0.8rem',
              fontWeight: 600,
              boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            }}
          >
            {cell ? cell.charAt(0).toUpperCase() : 'N'}
          </Avatar>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cell || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      dataField: 'category',
      text: 'Category',
      sort: true,
      headerStyle: () => ({ ...columnStyles.category, fontWeight: 600 }),
      style: columnStyles.category,
      formatter: (cell) => (
        <Chip
          label={cell ? cell.toUpperCase() : 'N/A'}
          size="small"
          color="primary"
          variant="filled"
          sx={{
            fontWeight: 500,
            fontSize: '0.78rem',
            height: 26,
            borderRadius: '6px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        />
      ),
    },
    {
      dataField: 'description',
      text: 'Description',
      sort: true,
      headerStyle: () => ({ ...columnStyles.description, fontWeight: 600 }),
      style: columnStyles.description,
      formatter: (cell) =>
        cell ? (
          <Tooltip title={cell} arrow placement="top">
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.5,
                fontWeight: 400,
              }}
            >
              {cell}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.disabled" fontStyle="italic">
            No description provided
          </Typography>
        ),
    },
    {
      dataField: 'representatives',
      text: 'Representatives',
      sort: false,
      headerStyle: () => ({ ...columnStyles.representatives, fontWeight: 600 }),
      style: columnStyles.representatives,
      formatter: (cell, row) => {
        const reps = row.representatives || [];

        if (reps.length === 0) {
          return (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              No contacts
            </Typography>
          );
        }

        const firstRep = reps[0];
        const displayName = firstRep.name || 'Contact';
        const displayInfo = firstRep.phone || firstRep.email || '';

        const tooltipContent = (
          <Box sx={{ p: 1, minWidth: 180 }}>
            {reps.map((rep, i) => (
              <Box key={i} sx={{ mb: 1.25 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {rep.name || `Contact ${i + 1}`}
                </Typography>
                {rep.phone && <Typography variant="caption">📞 {rep.phone}</Typography>}
                {rep.email && <Typography variant="caption">✉️ {rep.email}</Typography>}
              </Box>
            ))}
          </Box>
        );

        return (
          <Tooltip title={tooltipContent} arrow placement="top">
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <FeatherIcon icon="user" size={16} color="#555" />
                <Typography variant="body2" fontWeight={600} noWrap>
                  {displayName}
                </Typography>
              </Stack>
              {displayInfo && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  {displayInfo}
                </Typography>
              )}
              {reps.length > 1 && (
                <Chip
                  label={`+${reps.length - 1}`}
                  size="small"
                  sx={{
                    mt: 0.75,
                    height: 20,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    bgcolor: alpha('#1976d2', 0.1),
                    color: 'primary.main',
                  }}
                />
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      dataField: 'properties',
      text: 'Assigned Properties',
      sort: false,
      headerStyle: () => ({ ...columnStyles.properties, fontWeight: 600 }),
      style: columnStyles.properties,
      formatter: (cell, row) => {
        const props = row.properties || [];

        if (props.length === 0) {
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <FeatherIcon icon="globe" size={16} color="#555" />
              <Typography variant="body2" fontWeight={600} color="primary.main">
                Global Access
              </Typography>
            </Stack>
          );
        }

        if (props.length === 1) {
          const { building_name } = props[0];
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <FeatherIcon icon="home" size={16} color="#555" />
              <Tooltip title={building_name || 'Property'} arrow>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {building_name || 'Linked Property'}
                </Typography>
              </Tooltip>
            </Stack>
          );
        }

        const firstBuilding = props[0]?.building_name || 'Unnamed';
        const tooltipContent = (
          <Box sx={{ maxHeight: 240, overflowY: 'auto', p: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Assigned to {props.length} properties
            </Typography>
            {props.map((p, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <FeatherIcon icon="check-circle" size={14} color="#4caf50" />
                <Typography variant="body2">{p.building_name || 'Unnamed'}</Typography>
              </Stack>
            ))}
          </Box>
        );

        return (
          <Tooltip title={tooltipContent} arrow placement="top">
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <FeatherIcon icon="home" size={16} color="#555" />
                <Typography variant="body2" fontWeight={600}>
                  {props.length} Properties
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {firstBuilding} +{props.length - 1}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      dataField: 'actions',
      text: 'Actions',
      headerStyle: () => ({ ...columnStyles.actions, fontWeight: 600 }),
      style: columnStyles.actions,
      formatter: (cell, row) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Tooltip title="Edit Contractor" arrow>
            <IconButton
              onClick={() => onEdit(row)}
              size="small"
              sx={{
                color: 'primary.main',
                '&:hover': { bgcolor: alpha('#1976d2', 0.12) },
              }}
            >
              <FeatherIcon icon="edit-2" size={18} />
            </IconButton>
          </Tooltip>

          {isCompanyUser && (
            <Tooltip title="Delete Contractor" arrow>
              <IconButton
                onClick={() => onDelete(row)}
                size="small"
                sx={{
                  color: 'error.main',
                  '&:hover': { bgcolor: alpha('#d32f2f', 0.12) },
                }}
              >
                <FeatherIcon icon="trash-2" size={18} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  const paginationOptions = {
    sizePerPage: 10,
    hideSizePerPage: true,
    hidePageListOnlyOnePage: true,
  };

  const NoDataIndication = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        width: '100%',
        p: 4,
      }}
    >
      <Box textAlign="center">
        <FeatherIcon
          icon="inbox"
          size={64}
          strokeWidth={1.2}
          color="#bdbdbd"
          style={{ marginBottom: 24, opacity: 0.7 }}
        />
        <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
          No contractors found
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Adjust filters or add a new contractor to get started.
        </Typography>
      </Box>
    </Box>
  );

  const tableStyles = {
    borderRadius: 3,
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    bgcolor: 'background.paper',
    '& .react-bootstrap-table': {
      minHeight: '50vh',
    },
    '& table': {
      tableLayout: 'fixed',
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    '& thead th': {
      position: 'sticky',
      top: 0,
      backgroundColor: alpha('#f8f9fa', 0.98),
      backdropFilter: 'blur(8px)',
      zIndex: 10,
      borderBottom: '2px solid #e0e7ff',
      padding: '16px 14px',
      fontWeight: 700,
      fontSize: '0.9rem',
      color: '#1a3c5e',
      whiteSpace: 'nowrap',
    },
    '& tbody td': {
      padding: '16px 14px',
      borderBottom: '1px solid #f0f4f8',
      verticalAlign: 'middle',
      fontSize: '0.92rem',
    },
    '& tbody tr': {
      transition: 'all 0.18s ease',
      backgroundColor: 'white',
    },
    '& tbody tr:hover': {
      backgroundColor: '#f8fbff !important',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.08)',
    },
    '& .pagination': {
      padding: '16px 24px',
      borderTop: '1px solid #e0e7ff',
      backgroundColor: '#fafcff',
      justifyContent: 'flex-end',
    },
  };

  return (
    <ThemeProvider theme={tableTheme}><Paper sx={tableStyles} className="modern-table scrollable-table">
      <BootstrapTable
        keyField="_id"
        data={items || []}
        columns={columns}
        pagination={paginationFactory(paginationOptions)}
        bootstrap4
        bordered={false}
        hover
        noDataIndication={NoDataIndication}
        wrapperClasses="table-responsive"
      />
    </Paper>
    </ThemeProvider>
  );
};

export { ContractorsListTable };